import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  WALKTHROUGH_STEPS,
  getWalkthroughState,
  saveWalkthroughState,
} from './walkthrough-steps';

const WalkthroughContext = createContext(null);

export const WalkthroughProvider = ({ children }) => {
  const [state, setState] = useState(() => getWalkthroughState());
  const [isOpen, setIsOpen] = useState(false);

  const currentStep = WALKTHROUGH_STEPS[state.currentStep] || null;
  const totalSteps = WALKTHROUGH_STEPS.length;
  const isFirstStep = state.currentStep === 0;
  const isLastStep = state.currentStep === totalSteps - 1;

  const updateState = useCallback((updates) => {
    setState((prev) => {
      const newState = { ...prev, ...updates };
      saveWalkthroughState(newState);
      return newState;
    });
  }, []);

  const startWalkthrough = useCallback(() => {
    updateState({
      currentStep: 0,
      completed: false,
      dismissed: false,
      startedAt: new Date().toISOString(),
    });
    setIsOpen(true);
  }, [updateState]);

  const resumeWalkthrough = useCallback(() => {
    setIsOpen(true);
  }, []);

  const nextStep = useCallback(() => {
    if (state.currentStep < totalSteps - 1) {
      updateState({ currentStep: state.currentStep + 1 });
    }
  }, [state.currentStep, totalSteps, updateState]);

  const prevStep = useCallback(() => {
    if (state.currentStep > 0) {
      updateState({ currentStep: state.currentStep - 1 });
    }
  }, [state.currentStep, updateState]);

  const goToStep = useCallback(
    (stepIndex) => {
      if (stepIndex >= 0 && stepIndex < totalSteps) {
        updateState({ currentStep: stepIndex });
      }
    },
    [totalSteps, updateState]
  );

  const completeWalkthrough = useCallback(() => {
    updateState({ completed: true });
    setIsOpen(false);
  }, [updateState]);

  const dismissWalkthrough = useCallback(() => {
    updateState({ dismissed: true });
    setIsOpen(false);
  }, [updateState]);

  const closeWalkthrough = useCallback(() => {
    setIsOpen(false);
  }, []);

  const resetWalkthrough = useCallback(() => {
    updateState({
      currentStep: 0,
      completed: false,
      dismissed: false,
      startedAt: null,
    });
    setIsOpen(false);
  }, [updateState]);

  const value = {
    state,
    isOpen,
    currentStep,
    totalSteps,
    isFirstStep,
    isLastStep,
    steps: WALKTHROUGH_STEPS,
    startWalkthrough,
    resumeWalkthrough,
    nextStep,
    prevStep,
    goToStep,
    completeWalkthrough,
    dismissWalkthrough,
    closeWalkthrough,
    resetWalkthrough,
  };

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
    </WalkthroughContext.Provider>
  );
};

export const useWalkthrough = () => {
  const context = useContext(WalkthroughContext);
  if (!context) {
    console.warn('useWalkthrough must be used within a WalkthroughProvider');
    return {
      state: {
        currentStep: 0,
        completed: false,
        dismissed: false,
        startedAt: null,
      },
      isOpen: false,
      currentStep: null,
      totalSteps: 0,
      isFirstStep: true,
      isLastStep: true,
      steps: [],
      startWalkthrough: () => {},
      resumeWalkthrough: () => {},
      nextStep: () => {},
      prevStep: () => {},
      goToStep: () => {},
      completeWalkthrough: () => {},
      dismissWalkthrough: () => {},
      closeWalkthrough: () => {},
      resetWalkthrough: () => {},
    };
  }
  return context;
};
